// hydrate cart store on cart change
document.addEventListener('on:cart:change', () => {
  Alpine.store('cart').syncWithShopifyCart();
});

// hydrate cart store on cart add
document.addEventListener('on:cart:add', (event) => {
  Alpine.store('cart').syncWithShopifyCart();
});

document.addEventListener('alpine:init', () => { 
  Alpine.store('cart', {
    items: [],
    currentlyChangingVariantId: null,
    
    init() {
      // Fetch cart data once on initialization
      this.syncWithShopifyCart();
    },
    
    addItem(item) {
      this.items.push(item);
    },
    
    getQuantity(variantId) {
      return this.items.find((item) => item.variant_id === variantId)?.quantity || 0;
    },
    
    setChangingVariant(variantId) {
      this.currentlyChangingVariantId = variantId;
    },
    
    clearChangingVariant() {
      this.currentlyChangingVariantId = null;
    },
    
    syncWithShopifyCart() {
      fetch(`${theme.routes.cart}.js`)
        .then(response => response.json())
        .then(cart => {
          this.items = cart.items;
          document.dispatchEvent(new Event('alpine:cart:updated', { bubbles: true }));
        })
        .catch(error => console.error('Error fetching cart:', error));
    },
  })
    
  Alpine.data('CollectionView', (initialView) => ({
    view: initialView,
    defaultView: 'grid',
    minBreakpoint: 1245,
    
    init() {      
      if (window.innerWidth < this.minBreakpoint && this.view !== this.defaultView) {
        this.switchView(this.defaultView);
      }
    },
    
    updateCartAttribute(view) {
      fetch(theme.routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { view }
        })
      })
      .catch((error) => console.error('Error updating collection view:', error.message));
    },
    
    closeExpandedQuickBuys() {
      const expandedQuickBuys = document.querySelectorAll('.product-block.expanded');
      expandedQuickBuys.forEach((el) => {
        theme.quickbuy.contractDetail(el, true);
      });
    },
    
    switchView(newView) {
      // Prevent changing to non-default view on small screens
      if (window.innerWidth < this.minBreakpoint) {
        newView = this.defaultView;
      }
      
      this.view = newView;
      this.updateCartAttribute(newView);
      this.closeExpandedQuickBuys();      
    }
  }));

  Alpine.data('VariantPricing', ({ 
    variantId, 
    basePrice,
    minimumQuantity, 
    priceBreaks 
  }) => ({
    get currentPrice() {
      const cartQuantity = Alpine.store('cart').getQuantity(variantId);
      
      // Check each price break in descending order of quantity
      if (priceBreaks && priceBreaks.length > 0) {
        // Find the highest applicable price break
        for (const priceBreak of priceBreaks) {
          if (cartQuantity >= priceBreak.minimum_quantity) {
            return priceBreak.price;
          }
        }
        
        // If no price breaks match but we meet minimum quantity
        if (cartQuantity >= minimumQuantity) {
          return basePrice;
        }
      }
      
      // Default price if no quantity in cart or no price breaks
      return basePrice;
    }
  }));
  
  Alpine.data('TotalPrice', ({ variantId, initialTotalPrice }) => ({
    totalPrice: initialTotalPrice,
    
    init() {
      document.addEventListener('alpine:cart:updated', () => {
        const cartItem = Alpine.store('cart').items.find((item) => item.variant_id === variantId);
                
        if (cartItem) {
          this.totalPrice = Shopify.formatMoney(cartItem.line_price, window.theme.settings.moneyWithCurrencyFormat);
        } else {
          this.totalPrice = Shopify.formatMoney(0, window.theme.settings.moneyWithCurrencyFormat);
        }
      });
    }
  }));

  Alpine.data('QuantityHandler', ({ variantId, minQuantity, maxQuantity }) => ({
    addToCartTimeout: null,
    adjustCartTimeout: null,
    
    init() {
      this.quantityElement = this.$el.querySelector('input[name="quantity"]'); 
    },
    
    get canIncreaseQuantity() {
      const changingVariant = Alpine.store('cart').currentlyChangingVariantId;
      // Disable if another item is changing or if normal disable conditions apply
      return (changingVariant !== null && changingVariant !== variantId) || 
        maxQuantity <= minQuantity || 
        this.quantity >= maxQuantity;
    },
    
    get canDecreaseQuantity() {
      const changingVariant = Alpine.store('cart').currentlyChangingVariantId;
      // Disable if another item is changing or if normal disable conditions apply
      return (changingVariant !== null && changingVariant !== variantId) || 
        !this.quantity;
    },
    
    get quantity() {
      const cartQuantity = Alpine.store('cart').getQuantity(variantId);
      return cartQuantity;
    },
    
    get currentInputFieldValue() {
      // as other parts of the store rely on more global quantity-picker logic
      // we simply get the correctly accumulated value here,
      // instead of trying to replicate that logic here.
      
      // we cant use this.quantityElement because it's not updated yet
      return parseInt(
        this.$el.parentNode.querySelector('input[name="quantity"]').value, 
        10
      );
    },
    
    get preorderStatus() {
      const preorderStatus = this.$el.closest(`[data-variant-id="${variantId}"]`)
        .querySelector('input[name="_is_preorder"]');
      return preorderStatus ? preorderStatus.value : null;
    },

    debouncedAdjustCart(isButtonClick = false) {
      clearTimeout(this.adjustCartTimeout);
      
      // Set the lock to this variant
      Alpine.store('cart').setChangingVariant(variantId);
      
      this.adjustCartTimeout = setTimeout(() => {
        let quantityToAdjustTo = Math.min(this.currentInputFieldValue, maxQuantity);

        if (isButtonClick) {
          if (this.currentInputFieldValue <= minQuantity && minQuantity <= this.quantity) {
            quantityToAdjustTo = 0;
          }
        }
        
        this.adjustCartItem(quantityToAdjustTo);
      }, 500);
    },
    
    debouncedAddToCart() {
      clearTimeout(this.addToCartTimeout);
      
      // Set the lock to this variant
      Alpine.store('cart').setChangingVariant(variantId);      
      
      this.addToCartTimeout = setTimeout(() => {
        let quantityToAdd = this.currentInputFieldValue - this.quantity;
        let properties = {};
        
        if (quantityToAdd + this.quantity < minQuantity) {
          quantityToAdd = minQuantity;
        }

        if (this.preorderStatus) {
          properties['_is_preorder'] = this.preorderStatus;
        }

        this.addToCart(quantityToAdd, properties);
      }, 500);
    },
    
    addToCart(quantity, properties) {
      if (quantity < 1) return;

      const acceptedQuantity = this.quantity;
      fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: quantity,
            properties: properties
          }]
        })
      })
      .then(response => {
        if (!response.ok) {
          return response.json().then((data) => {
            const input = this.$el.parentNode.querySelector('input[name="quantity"]');
            if (input) {
              input.value = acceptedQuantity;
            }
            const msg = (data && data.description)
              ? data.description
              : theme.strings.cartItemsQuantityError.replace('[QUANTITY]', acceptedQuantity);
            theme.showQuickPopup(msg, input || this.$el);
            Alpine.store('cart').syncWithShopifyCart();
            Alpine.store('cart').clearChangingVariant();
          });
        }
        return response.json().then((data) => {
          const { title } = data.items[0];
          this.onAddedToCart({ title, quantity, variantId });
        });
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        Alpine.store('cart').clearChangingVariant();
      });
    },
    
    adjustCartItem(qty) {
      // Using change.js instead of update.js so Shopify enforces add-to-cart
      // limits and inventory constraints (update.js bypasses these for existing
      // cart items).
      const acceptedQuantity = this.quantity;
      fetch(theme.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: String(variantId), quantity: parseInt(qty, 10) })
      })
      .then(response => {
        if (!response.ok) {
          return response.json().then((data) => {
            const input = this.$el.parentNode.querySelector('input[name="quantity"]');
            if (input) {
              input.value = acceptedQuantity;
            }
            const msg = (data && data.description)
              ? data.description
              : theme.strings.cartItemsQuantityError.replace('[QUANTITY]', acceptedQuantity);
            theme.showQuickPopup(msg, input || this.$el);
            Alpine.store('cart').syncWithShopifyCart();
            Alpine.store('cart').clearChangingVariant();
          });
        }
        this.onRemovedFromCart();
      })
      .catch((error) => {
        console.error('Error adjusting cart item:', error);
        Alpine.store('cart').clearChangingVariant();
      });
    },
    
    onAddedToCart({ title, quantity, variantId }) {
      /**
        The following is very theme specific and is copied from main.js
       */

      // handles cart notification instead of open drawer
      if (theme.settings.afterAddToCart === 'notification') {
        const notification = document.getElementById('AddedNotification').content.firstElementChild.cloneNode(true);
        notification.dataset.productTitle = `${quantity}x ${title}`;
        let notificationContainer = document.querySelector('.pageheader--sticky');
        if (!notificationContainer) {
          notificationContainer = document.querySelector('body');
        }
        notificationContainer.appendChild(notification);
      } else if (theme.settings.afterAddToCart === 'drawer') {
        document.querySelector('.js-cart-drawer').open();
      }
      
      document.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: {
          variantId: variantId
        }
      })); 
      
      // refreshes items in cart drawer
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      
      // Release the lock
      Alpine.store('cart').clearChangingVariant();
    },
    
    onRemovedFromCart() {
      // refreshes items in cart drawer
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      
      // Release the lock
      Alpine.store('cart').clearChangingVariant();
    },
  }));
});